import { checkPermissions } from "@elit-hair/elit-tools";
import { SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu } from "@elit-hair/elit-ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { appRoutes } from "@/pages/app-routes";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/use-dashboard-role";
import type { DashboardRole } from "@/pages/home/components/role-dashboard/types/dashboard-role.types";
import type { NavigationRoute } from "@/types/navigation-route";
import { isDevEnvironment } from "@/utils/is-dev-environment";

import { SidebarMenuItem } from "./sidebar-menu-item";
import type { NavItem } from "./types/nav-item.types";
import { filterNavItemsByRole } from "./utils/filter-nav-by-role";

function mapRouteToNavItem(route: NavigationRoute): NavItem {
    const children = route.children
        ?.filter((child) => child.meta.visibleInSidebar)
        .filter((child) => !child.meta.devOnly || isDevEnvironment())
        .sort((a, b) => a.meta.order - b.meta.order)
        .map(mapRouteToNavItem);

    return {
        id: route.to ?? route.path ?? route.meta.label,
        to: route.to ?? route.path,
        title: route.meta.label,
        icon: route.meta.icon,
        permissions: route.meta.permissions,
        permissionMode: route.meta.permissionMode,
        order: route.meta.order,
        children,
    };
}

function sortNavItems(items: NavItem[]): NavItem[] {
    return items
        .slice()
        .sort((a, b) => {
            const aOrder = a.order;
            const bOrder = b.order;
            const aHas = typeof aOrder === "number";
            const bHas = typeof bOrder === "number";
            if (aHas && bHas) return aOrder - bOrder;
            if (aHas) return -1;
            if (bHas) return 1;
            return 0;
        })
        .map((item) => {
            if (item.children && item.children.length > 0) {
                return { ...item, children: sortNavItems(item.children) };
            }
            return item;
        });
}

function collectNavTree(): NavItem[] {
    const visibleRoutes = appRoutes
        .filter((route) => route.meta.visibleInSidebar)
        .filter((route) => !route.meta.devOnly || isDevEnvironment())
        .sort((a, b) => a.meta.order - b.meta.order);

    const items = visibleRoutes.map(mapRouteToNavItem);
    return sortNavItems(items);
}

function filterByPermissions(items: NavItem[], permissionCodes: string[] | undefined): NavItem[] {
    return items
        .map((item) => {
            const allowed = checkPermissions(permissionCodes, item.permissions, item.permissionMode);
            if (!allowed) return null;

            if (item.children && item.children.length > 0) {
                const children = filterByPermissions(item.children, permissionCodes);
                if (children.length === 0) return null;
                return { ...item, children };
            }

            return item;
        })
        .filter((item): item is NavItem => item !== null);
}

export function getVisibleNavItems(permissionCodes: string[] | undefined, role: DashboardRole): NavItem[] {
    return filterNavItemsByRole(filterByPermissions(collectNavTree(), permissionCodes), role);
}

function NavMenuItems({ items }: { items: NavItem[] }) {
    return (
        <SidebarMenu>
            {items.map((item) => (
                <SidebarMenuItem key={item.id} {...item} />
            ))}
        </SidebarMenu>
    );
}

export function NavigationItems() {
    const { t } = useTranslation();
    const { role, permissionCodes } = useDashboardRole();
    const items = useMemo(() => getVisibleNavItems(permissionCodes, role), [permissionCodes, role]);

    const primaryItems = items.filter((item) => !item.children?.length);
    const sectionItems = items.filter((item) => (item.children?.length ?? 0) > 0);

    return (
        <>
            {primaryItems.length > 0 && (
                <SidebarGroup>
                    <SidebarGroupContent>
                        <NavMenuItems items={primaryItems} />
                    </SidebarGroupContent>
                </SidebarGroup>
            )}

            {sectionItems.map((section) => (
                <SidebarGroup key={section.id}>
                    <SidebarGroupLabel>{t(section.title || "")}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <NavMenuItems items={section.children ?? []} />
                    </SidebarGroupContent>
                </SidebarGroup>
            ))}
        </>
    );
}
